import React, { useEffect, useState } from 'react';
import {
  Grid,
  styled,
  TextField,
  Paper,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import PageHeader from '../../../components/PageHeader';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Gate, ShiftType } from '../../../schema/gate';
import GateTypeFromInput from '../../../components/formInput/GateTypeFromInput';
import { api } from '../../../api';
import AreaCodeFromInput from '../../../components/formInput/AreaCodeFromInput';
import { Area } from '../../../schema/area';

const EditGate: React.FC = () => {
  const navigate = useNavigate();
  const { gate_id } = useParams();

  const [name, setName] = useState<string>('');
  const [gateTypeId, setGateTypeId] = useState<number | null>(null);
  const [areaID, setAreaID] = useState<number | null>(null);
  const [shiftTypeID, setShiftTypeID] = useState<number | null>(null);
  const [isMain, setIsMain] = useState<boolean>(false);
  const [accDeviceId, setAccDeviceId] = useState<string>('');
  const [accDeviceName, setAccDeviceName] = useState<string>('');
  const [lat, setLat] = useState<string>('');
  const [long, setLong] = useState<string>('');

  const [shiftTypeList, setShiftTypeList] = useState<ShiftType[]>([]);
  const [areaName, setAreaName] = useState<string>('');

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) {
      return;
    }
    if (name && gateTypeId != null && areaID != null && shiftTypeID != null) {
      setIsLoading(true);
      api
        .put(`/gate/${gate_id}`, {
          gate_name: name,
          gate_type_id: gateTypeId,
          area_id: areaID,
          shift_type_id: shiftTypeID,
          is_main: isMain,
          acc_device_id: accDeviceId,
          acc_device_name: accDeviceName,
          lat: lat ? parseFloat(lat) : null,
          long: long ? parseFloat(long) : null
        })
        .then(() => {
          navigate('/gate');
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleDelete = () => {
    if (isLoading) {
      return;
    }
    if (gate_id) {
      setIsLoading(true);
      api
        .delete(`/gate/${gate_id}`)
        .then(() => {
          navigate('/gate');
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    api.get<Gate>(`/gate/${gate_id}`).then((res) => {
      const { data } = res;
      setName(data.gate_name);
      setGateTypeId(data.gate_type_id ? data.gate_type_id : null);
      setAreaID(data.area_id ? data.area_id : null);
      setAccDeviceId(data.acc_device_id);
      setAccDeviceName(data.acc_device_name);
      setIsMain(!!data.is_main);
      setLat(`${data.lat}`);
      setLong(`${data.long}`);
      setShiftTypeID(data.shift_type_id ? data.shift_type_id : null);
    });
  }, []);
  useEffect(() => {
    if (areaID) {
      api.get<Area>(`/area/${areaID}`).then((res) => {
        const { data } = res;
        setAreaName(data.area_name);
      });
      return;
    }
    setAreaName('');
  }, [areaID]);

  useEffect(() => {
    api.get<ShiftType[]>(`/shift_type`).then((res) => {
      const { data } = res;
      setShiftTypeList(data);
    });
  }, []);
  return (
    <RootContainer>
      <PageHeader text="แก้ไขประตู" />

      <Button component={Link} to={`/gate`} sx={{ my: 3, p: 0 }}>
        <ArrowBackIosIcon />
        ประตู
      </Button>

      <Paper>
        <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
          {'แก้ไขข้อมูล'}
        </Typography>

        <Form onSubmit={handleFormSubmit} sx={{ padding: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ชื่อประตู"
                variant="outlined"
                placeholder="ชื่อประตู"
                onChange={(e) => setName(e.target.value ? e.target.value : '')}
                value={name}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <GateTypeFromInput
                value={gateTypeId}
                onChange={(value) => setGateTypeId(value)}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>{`ชนิดประตู`}</InputLabel>
                <Select
                  label={`ชนิดประตู`}
                  variant="outlined"
                  onChange={(e) => {
                    const parsed = e.target.value;
                    if (parsed === 'true') {
                      setIsMain(true);
                      return;
                    }
                    setIsMain(false);
                  }}
                  value={isMain ? 'true' : 'false'}
                  autoComplete="off"
                  required
                >
                  <MenuItem value={'true'}>เข้า-ออก</MenuItem>
                  <MenuItem value={'false'}>ทั่วไป</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <AreaCodeFromInput value={areaID} onChange={(value) => setAreaID(value)} required />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ชื่อพื้นที่"
                variant="outlined"
                placeholder="ชื่อพื้นที่"
                value={areaName ? areaName : ''}
                InputProps={{ readOnly: true }}
                disabled={true}
                sx={
                  areaName
                    ? {
                        '.Mui-disabled': {
                          color: 'black',
                          opacity: 1,
                          '-webkit-text-fill-color': 'black'
                        },
                        '.MuiInputLabel-root': {
                          color: '#65748B',
                          '-webkit-text-fill-color': '#65748B'
                        }
                      }
                    : null
                }
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>{`ช่วงเวลา เปิด-ปิด`}</InputLabel>
                <Select
                  label={`ช่วงเวลา เปิด-ปิด`}
                  variant="outlined"
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value);
                    if (isNaN(parsed)) {
                      setShiftTypeID(null);
                      return;
                    }
                    setShiftTypeID(parsed);
                  }}
                  value={shiftTypeID != null ? `${shiftTypeID}` : ''}
                  autoComplete="off"
                  required
                >
                  {shiftTypeList.map((data, index) => (
                    <MenuItem key={index} value={`${data.id}`}>
                      {`${data.open_time?.substring(0, 5)} - ${data.close_time?.substring(0, 5)} (${
                        data.type_name
                      })`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={4}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ACC Device ID"
                variant="outlined"
                placeholder="ACC Device ID"
                onChange={(e) => setAccDeviceId(e.target.value ? e.target.value : '')}
                value={accDeviceId}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ACC Device Name"
                variant="outlined"
                placeholder="ACC Device Name"
                onChange={(e) => setAccDeviceName(e.target.value ? e.target.value : '')}
                value={accDeviceName}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Latitude"
                variant="outlined"
                placeholder="Latitude"
                onChange={(e) => setLat(e.target.value ? e.target.value : '')}
                value={lat}
                type="number"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Longitude"
                variant="outlined"
                placeholder="Longitude"
                onChange={(e) => setLong(e.target.value ? e.target.value : '')}
                value={long}
                type="number"
              />
            </Grid>
          </Grid>

          <Grid container columns={2} spacing={2} direction={'row-reverse'}>
            <Grid item>
              <Button type="submit" variant="contained" color="primary" sx={{ marginTop: 4 }}>
                แก้ไข
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="error"
                sx={{ marginTop: 4 }}
                onClick={handleDelete}
              >
                ลบ
              </Button>
            </Grid>
          </Grid>
        </Form>
      </Paper>
    </RootContainer>
  );
};

export default EditGate;

const RootContainer = styled('div')`
  padding: 24px;
`;

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  align-items: end;
`;
